import { useState } from 'react'
import Check from '@mui/icons-material/Check'
import Delete from '@mui/icons-material/Delete'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import MoreVert from '@mui/icons-material/MoreVert'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { QueryKey } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import useViewerId from '@/hooks/useViewerId'
import { useGetApprovalPolicy } from '@/service-library/hooks/approval-policies'
import { ApprovalStatePolicy, OnBehalfOf } from '@/types/approvals'
import { AuthUser } from '@/types/users'
import {
  approvalsRemaining,
  getApprovalEligibility,
} from '@/utils/approval-actions'
import {
  approverRowUserId,
  statePolicyApproverRows,
} from '@/utils/approver-entries'
import { orderChipLabel } from '@/utils/policy-labels'
import ApprovalApproverRow from './ApprovalApproverRow'
import ApprovalPolicyDetailsDialog from './ApprovalPolicyDetailsDialog'
import UpdatePolicyVersionDialog from './UpdatePolicyVersionDialog'

type ApprovalPolicyProgressCardProps = {
  policy: ApprovalStatePolicy
  documentId: string
  /** The approval state's `state_version_id` — the update-to-latest write fails if the document changed after this was read. */
  stateVersionId?: string
  approvalStateQueryKey?: QueryKey
  /** Refetch the approval state — called when the update fails because the document changed underneath it. */
  onStaleState?: () => void
  /** Approvers by pd user id — see `useUsersById`. */
  usersById: Map<string, AuthUser>
  usersPending: boolean
  onApprove?: (policyId: string, onBehalfOf?: OnBehalfOf) => void
  onReject?: (
    policyId: string,
    reason: string | null,
    onBehalfOfUserId?: string,
  ) => void
  /** Takes this policy off the document — the policy itself is untouched. The parent owns the write so it lines up behind approve and reject. */
  onRemovePolicy?: (policyId: string) => void
  /** True while any policy removal is in flight — removals run one at a time. */
  isRemovingPolicy?: boolean
  /** Policies currently being written — only those policies' rows disable their controls. */
  pendingPolicyIds?: Set<string>
}

type ProgressIndicatorProps = {
  policy: ApprovalStatePolicy
  approverCount: number
  /** Position of the policy's `next_required` entry in the approver list, or -1 when it's null or unmatched. */
  currentStepIndex: number
}

function ProgressIndicator({
  policy,
  approverCount,
  currentStepIndex,
}: ProgressIndicatorProps) {
  if (policy.is_rejected) {
    return <Chip size="small" color="error" label="Rejected" />
  }

  if (policy.satisfied) {
    return (
      <Chip size="small" color="success" icon={<Check />} label="Complete" />
    )
  }

  // pd-approval couldn't read the live policy, so any count or step indicator
  // would be a guess. Say why the policy can't be satisfied instead.
  if (!policy.config_resolved) {
    return (
      <Typography variant="caption" color="text.secondary" noWrap>
        Policy details unavailable
      </Typography>
    )
  }

  // An open sequential policy should always name a next approver. If the data
  // is bad and it doesn't, show nothing rather than a misleading "Step 0".
  if (policy.approval_order === 'sequential' && currentStepIndex !== -1) {
    return (
      <Typography
        color="info.main"
        noWrap
        sx={{ fontSize: 13, fontWeight: 500 }}
      >
        Step {currentStepIndex + 1} of {approverCount}
      </Typography>
    )
  }

  if (policy.approval_order === 'sequential') {
    return null
  }

  const remaining = approvalsRemaining(policy)

  return (
    <Typography variant="body2" component="span">
      <Box component="span" sx={{ fontWeight: 600 }}>
        {remaining}
      </Box>{' '}
      <Typography component="span" variant="caption" color="text.secondary">
        remaining
      </Typography>
    </Typography>
  )
}

export default function ApprovalPolicyProgressCard({
  policy,
  documentId,
  stateVersionId,
  approvalStateQueryKey,
  onStaleState,
  usersById,
  usersPending,
  onApprove,
  onReject,
  onRemovePolicy,
  isRemovingPolicy,
  pendingPolicyIds,
}: ApprovalPolicyProgressCardProps) {
  const viewerId = useViewerId()
  const [isRejectFormOpen, setIsRejectFormOpen] = useState(false)
  const detailsOverlay = useOverlay()
  const updateOverlay = useOverlay()
  const menuOverlay = useOverlay()

  // The pin only matters if the policy has moved past it, and only the live
  // policy knows where "latest" is — so it's fetched just for pinned documents.
  const pinnedVersionId = policy.pinned_approval_policy_version_id
  const { approvalPolicy: livePolicy } = useGetApprovalPolicy({
    id: policy.approval_policy_id,
    enabled: !!pinnedVersionId,
  })
  const isBehindLatest =
    !!pinnedVersionId &&
    !!livePolicy?.current_version_id &&
    livePolicy.current_version_id !== pinnedVersionId

  // The same permission that gates editing the policy. An approver must never
  // see the button, or they could apply a version that lowers the bar for
  // their own document.
  const { isAuthorized: canEditPolicies } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )
  const approverRows = statePolicyApproverRows(policy)
  // The details dialog only has position ids from the live policy config; the
  // approval state already carries each one's name, so build the map here
  // instead of having the dialog fetch the position catalog. Leaving out an
  // entry with no name lets the dialog fall back to "Unknown position"
  // rather than showing a blank one.
  const positionNamesById = new Map(
    policy.approvers.flatMap((approver) =>
      approver.kind === 'position' && approver.position_name
        ? [[approver.position_id, approver.position_name] as const]
        : [],
    ),
  )
  // -1 on an any-order policy, or when next_required matches nobody. Use the
  // matched row's own stepNumber, not its index in this array: a position
  // entry can produce several rows that all share one stepNumber, so the
  // two numbers can differ once a position is in the list.
  const currentStep = approverRows.find((row) => row.isCurrentTurn)
  const currentStepIndex = currentStep ? currentStep.stepNumber - 1 : -1
  const orderLabel = orderChipLabel(policy)
  const eligibility = getApprovalEligibility(policy, viewerId)
  const isActionPending = !!pendingPolicyIds?.has(policy.approval_policy_id)
  const hasBlockedSlot = approverRows.some((row) => !!row.blocked)

  return (
    <Box
      sx={{
        border: 1,
        borderColor: isRejectFormOpen
          ? 'info.main'
          : policy.is_rejected
          ? 'error.main'
          : policy.satisfied
          ? 'success.main'
          : 'divider',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
        <Typography
          variant="subtitle1"
          noWrap
          sx={{ minWidth: 0, fontWeight: 600 }}
          title={policy.description}
        >
          {policy.description}
        </Typography>
        {orderLabel && (
          <Chip
            size="small"
            label={orderLabel}
            sx={{ flex: 'none', fontWeight: 600, letterSpacing: '0.04em' }}
          />
        )}
        {/* `ml: 'auto'` doesn't work here — Stack's spacing rule is a more
            specific selector and overrides a child's own margin-left. */}
        <Box
          sx={{ flex: '1 0 auto', textAlign: 'right', whiteSpace: 'nowrap' }}
        >
          <ProgressIndicator
            policy={policy}
            approverCount={policy.approvers.length}
            currentStepIndex={currentStepIndex}
          />
        </Box>

        <IconButton
          size="small"
          aria-label={`Policy actions for ${policy.description}`}
          onClick={menuOverlay.open}
          sx={{ flex: 'none' }}
        >
          <MoreVert sx={{ fontSize: 18 }} />
        </IconButton>
        <Menu
          anchorEl={menuOverlay.anchorEl as Element}
          open={menuOverlay.isOpen}
          onClose={menuOverlay.close}
        >
          <MenuItem
            onClick={() => {
              detailsOverlay.open()
              menuOverlay.close()
            }}
          >
            <ListItemIcon>
              <InfoOutlined fontSize="small" />
            </ListItemIcon>
            View Policy
          </MenuItem>
          {canEditPolicies && onRemovePolicy && (
            <MenuItem
              disabled={!stateVersionId || isActionPending || isRemovingPolicy}
              onClick={() => {
                onRemovePolicy(policy.approval_policy_id)
                menuOverlay.close()
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" color="error" />
              </ListItemIcon>
              <Typography component="span" color="error">
                Remove policy
              </Typography>
            </MenuItem>
          )}
        </Menu>
      </Stack>

      {/* Pulled out by the rows' own px so the status circles line up with the
          title, while the current-turn highlight still bleeds past the text. */}
      <Stack sx={{ mx: -1 }}>
        {approverRows.map((row) => {
          const userId = approverRowUserId(row)
          const isPositionRow = row.ref.kind === 'position'
          const isViewerRow = isPositionRow
            ? !!viewerId && !!row.slotUserIds?.includes(viewerId)
            : userId === viewerId
          // pd-approval only lets someone act for an approver named on the
          // policy, and never lets an approver on the same policy act for
          // another one. It also refuses while any position on the policy is
          // unresolved, since the viewer might be the one holding it.
          // Offering the button anywhere else would only fail.
          const canActOnBehalf =
            !hasBlockedSlot &&
            !!onApprove &&
            !!onReject &&
            canEditPolicies &&
            !!userId &&
            !isPositionRow &&
            !isViewerRow &&
            !eligibility.isApprover &&
            getApprovalEligibility(policy, userId).canAct
          const actedByUserId = row.latestAction?.acted_by_user_id

          return (
            <ApprovalApproverRow
              key={row.key}
              latestAction={row.latestAction}
              positionName={isPositionRow ? row.positionName : undefined}
              startingPoints={row.startingPoints}
              holders={row.holders}
              blocked={row.blocked}
              user={userId ? usersById.get(userId) : undefined}
              isUserPending={usersPending}
              isViewer={isViewerRow}
              approvalOrder={row.approvalOrder}
              stepNumber={row.stepNumber}
              isCurrentTurn={row.isCurrentTurn}
              isFutureStep={row.isFutureStep}
              notNeeded={row.notNeeded}
              eligibility={eligibility}
              isActionPending={isActionPending}
              onApprove={() => onApprove?.(policy.approval_policy_id)}
              onReject={(reason) =>
                onReject?.(policy.approval_policy_id, reason)
              }
              onRejectFormOpenChange={setIsRejectFormOpen}
              canActOnBehalf={canActOnBehalf}
              onApproveOnBehalf={(reason) =>
                userId &&
                onApprove?.(policy.approval_policy_id, {
                  userId,
                  reason,
                })
              }
              onRejectOnBehalf={(reason) =>
                userId && onReject?.(policy.approval_policy_id, reason, userId)
              }
              actedByUser={
                actedByUserId ? usersById.get(actedByUserId) : undefined
              }
            />
          )
        })}
      </Stack>

      {hasBlockedSlot && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          One or more positions on this policy couldn&apos;t be resolved to an
          approver. See the affected rows above for why.
        </Alert>
      )}

      {isBehindLatest && canEditPolicies && stateVersionId && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
          {policy.satisfied ? (
            // Everyone has signed off, so updating would only throw approvals
            // away. Say it's behind, offer no button.
            <Typography variant="caption" color="text.secondary">
              This document is using an older version of this policy.
            </Typography>
          ) : (
            <Button
              size="small"
              variant="text"
              color="secondary"
              onClick={updateOverlay.open}
            >
              Update to latest policy version
            </Button>
          )}
        </Stack>
      )}

      <ApprovalPolicyDetailsDialog
        overlay={detailsOverlay}
        policy={policy}
        positionNamesById={positionNamesById}
        usersById={usersById}
        usersPending={usersPending}
      />

      {isBehindLatest && stateVersionId && (
        <UpdatePolicyVersionDialog
          overlay={updateOverlay}
          policy={policy}
          documentId={documentId}
          stateVersionId={stateVersionId}
          approvalStateQueryKey={approvalStateQueryKey}
          onStaleState={onStaleState}
        />
      )}
    </Box>
  )
}
